import defaultPackageImage from "assests/images/package.png";
import ItemPropertiesDialog from "components/checkout/dialogs/ItemPropertiesDialog";
import ItemVariantSelectorDialog from "components/checkout/dialogs/ItemVariantSelectorDialog";
import RemoveItemDialog from "components/checkout/dialogs/RemoveItemDialog";
import LinkButton from "components/common/buttons/LinkButton";
import { GenericDialog } from "components/common/dialogs/GenericDialog";
import FullPageCarousel from "@shopflo/ui/full-page-carousel";
import { CarouselItem } from "@shopflo/ui/carousel";
import OverlaySpinner from "components/common/loaders/OverlaySpinner";
import Price from "components/common/Price";
import { useAuthContext } from "lib/contexts/AuthProvider";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { useUserContext } from "lib/contexts/UserProvider";
import { useWishlistContext } from "lib/contexts/WishlistProvider";
import { useIsSubscribeMode } from "lib/hooks/useIsSubscribeMode";
import { handleWishlistAddItem, handleWishlistRemoveItem } from "lib/utils/wishlist-v2";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { useItemCardAlign } from "lib/hooks/useItemCardAlign";
import { useItemEdit } from "lib/hooks/useItemEdit";
import { useJsonPermission } from "lib/hooks/useJsonPermission";
import { useLocale } from "lib/hooks/useLocale";
import { hexToRgba } from "lib/utils/helpers";
import { AddOnMetaType, ItemType } from "lib/types/checkout";
import { getFileredItemProperties, isImmutablePriceBand, showAddOnLineItems } from "lib/utils/checkout";
import { analyticsEvents, eventTypes } from "lib/utils/constants";
import { currencyFormatter } from "lib/utils/formatters";
import {
  classNames,
  getModifiedImageUrl,
  isEmptyObj,
  shouldRedirectToParent,
  publishPostMessage,
  truncateString,
} from "lib/utils/helpers";
import { errorToast, infoToast, successToast } from "lib/utils/toasters";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CaretDown, PencilSimple, Gift, Heart, Minus, Plus, Trash, Wind, Truck } from "@phosphor-icons/react";
import { AddOnItem } from "../common/AddOnItem";
import { BundleItem } from "./BundleItem";
import { Icon } from "@shopflo/ui/icon";
import { IconButton } from "@shopflo/ui/icon-button";
import { QuantitySelector } from "components/common/QuantitySelector";
import { AddOnSelectItem } from "components/common/AddOnSelectItem";
import { useToken } from "lib/contexts/TokenProvider";
import { OrderSummaryConfigType } from "lib/types/merchantConfigTypes";
import { getStyleFromCSSVariable, getTextColorByBrightness } from "@shopflo/ui/utils";
import ShippingDeliveryIcon, { ShippingDeliveryIconType } from "components/common/ShippingHandleIcon";
import { usePopupOrchestrator } from "lib/contexts/PopupOrchestratorProvider";
import { PopupEntry } from "lib/types/popup";
import { ItemPropertyValue } from "lib/types/itemPropertiesDialog";

interface ItemCardProps extends ItemType {
  disableOOSItems: boolean;
  checkoutItemsMutable: boolean;
  isCartItem?: boolean;
  isUpdating?: boolean;
  setIsUpdating?: React.Dispatch<React.SetStateAction<boolean>>;
  itemLevelAddons?: AddOnMetaType[];
  isUnservicable?: boolean;
  customisations?: OrderSummaryConfigType;
  isItemPropertiesAvailable?: boolean;
  blockVarientSelector?: boolean;
}

const MIN_ITEM_QUANTITY = 1;

export const ItemCard: React.FC<ItemCardProps> = (props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [propertyImagePreview, setPropertyImagePreview] = useState<{
    images: string[];
    startIndex: number;
  } | null>(null);
  const [isVariantSelectorOpen, setIsVariantSelectorOpen] = useState(false);

  const { t } = useLocale();
  const {
    state: { merchant, cartMetadata, checkoutMetadata },
  } = useMerchantContext();
  // ItemCard renders on both checkout and cart, so read line-item flags from whichever
  // context's ui-attributes is populated for the current page.
  const layoutMeta =
    checkoutMetadata?.uiAttributes?.layout?.metadata ?? cartMetadata?.uiAttributes?.layout?.metadata;
  const itemPropertiesConfig =
    checkoutMetadata?.uiAttributes?.itemProperties ?? cartMetadata?.uiAttributes?.itemProperties;
  const {
    state: { isAuthenticated, isLoggedInOnStore },
  } = useAuthContext();
  const {
    state: { user },
  } = useUserContext();
  const {
    state: { checkoutId, billing, checkoutValidations, availableAddOns, itemLevelETD, itemVariants, isC2P },
    actions: { setCartDialog, setCheckoutValidations, setDiscountProductSelectorModal },
  } = useCheckoutContext();

  const wishlistConfig = merchant?.wishlistConfig;
  const {
    state: { tokenId },
  } = useToken();

  const {
    item_title,
    quantity,
    price,
    originalPrice,
    markupPrice,
    image,
    variant_title,
    is_freebie,
    is_available,
    disableOOSItems,
    hasProductSelector,
    item_properties,
    availableQuantity,
    item_id,
    variant_id,
    product_id,
    isMutable,
    checkoutItemsMutable,
    isCartItem = false,
    isUpdating = false,
    setIsUpdating,
    item_url,
    is_discount_alteration,
    is_platform_fee,
    appliedItemAddOns,
    itemLevelAddons,
    isUnservicable = false,
    rawItem,
    has_components,
    components,
    customisations,
    isItemPropertiesAvailable,
    blockVarientSelector = false,
  } = props;

  const isRowAligned = useItemCardAlign(isCartItem) === "row";

  const {
    state: { wishlistedItems },
    actions: { isWishlisted, refreshWishlist },
  } = useWishlistContext();

  const isSubscribeMode = useIsSubscribeMode();
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const { getItemModificationRules } = useJsonPermission();

  const isBundleItem = has_components && components.length > 0;
  const variantTitle = variant_title?.slice(0, 35) + (variant_title?.length > 35 ? "..." : "");

  const { showDelete, showIncrement, showDecrement, redirectOnClick, showQuantityEditor } = useMemo(
    () => getItemModificationRules(rawItem),
    [getItemModificationRules, rawItem],
  );

  const finalItemPrice: number = parseFloat(price) * quantity;
  const finalCompareAtPrice: number =
    parseFloat(Boolean(layoutMeta?.showOriginalPrice) ? originalPrice : markupPrice) * quantity;
  const wishlistIconFillStyle = getStyleFromCSSVariable("--flo-primary-button-color", "fill");

  const {
    handleItemEdit,
    isLoading,
    showFullScreenLoader,
    openRemoveItemDialog,
    setOpenRemoveItemDialog,
    handleItemDeleteSubmit,
    handleAddOnEdit,
    handleVariantSwap,
  } = useItemEdit({
    item_id,
    quantity,
    availableQuantity,
    isCartItem,
    variant_id,
    billing,
    setIsUpdating,
  });

  const { openPopup, closePopup, activePopup } = usePopupOrchestrator();

  // Single source of truth for "is this item's properties dialog open" - the orchestrator only ever has one activePopup app-wide, so closing it (e.g. on cart close) closes this for free, instead of relying on N independent per-item booleans that can each drift out of sync.
  const isItemPropertiesDialogOpen =
    activePopup?.key === "ITEM_PROPERTIES" &&
    (activePopup as PopupEntry<"ITEM_PROPERTIES">).payload.itemId === item_id;

  // The full-page image preview is only ever opened from within this item's own properties dialog, so it should never survive that dialog being closed by something else.
  useEffect(() => {
    if (!isItemPropertiesDialogOpen) {
      setPropertyImagePreview(null);
    }
  }, [isItemPropertiesDialogOpen]);

  // Cart only. Checkout can run as a standalone page rather than inside the
  // cart iframe, so wishlist is not offered there.

  // Both sessions must hold: isAuthenticated is this iframe's, isLoggedInOnStore
  // is the store's. Undefined means the storefront script has not answered yet,
  // so it stays hidden until it does rather than flashing in and out.
  const productVariants = itemVariants?.[product_id] ?? [];
  const isVariantSelectorEnabled =
    isRowAligned &&
    productVariants.length > 1 &&
    !Boolean(is_freebie) &&
    !isBundleItem && !blockVarientSelector

  const isWishlistEnabled = useMemo(() => {
    if (!isCartItem) return false;
    if (!isAuthenticated || !Boolean(isLoggedInOnStore)) return false;
    if (isSubscribeMode) return false;
    return Boolean(wishlistConfig?.isEnabled) && Boolean(wishlistConfig?.config?.cart?.enabled);
  }, [isCartItem, isAuthenticated, isLoggedInOnStore, wishlistConfig, isSubscribeMode]);

  // The item-property transformer is configured for the merchant when any value parameter on item_properties comes back as an object (vs. a plain string).
  const isItemPropertyTransformerConfigured = useMemo(
    () => Object.values(item_properties ?? {}).some((value) => typeof value === "object" && value !== null),
    [item_properties],
  );

  const properties = useMemo(
    () =>
      getFileredItemProperties(
        item_properties,
        itemPropertiesConfig ?? [],
        layoutMeta?.showAllItemProperties ?? false,
        isItemPropertyTransformerConfigured,
      ),
    [item_properties, itemPropertiesConfig, layoutMeta?.showAllItemProperties],
  );

  // Stable references so ItemPropertiesDialog's React.memo can actually bail out when unrelated ItemCard re-renders happen while the properties dialog is open.
  const itemPropertiesDialogDetails = useMemo(
    () => ({
      item_title,
      image,
      variant_title,
      finalPrice: finalItemPrice,
      finalCompareAtPrice: finalCompareAtPrice,
    }),
    [item_title, image, variant_title, finalItemPrice, finalCompareAtPrice],
  );

  const handlePropertyImageClick = useCallback(
    (images: string[], startIndex: number) => setPropertyImagePreview({ images, startIndex }),
    [],
  );

  const isItemInWishlist = useMemo(() => {
    return isWishlisted(variant_id);
  }, [wishlistedItems, variant_id]);

  const getItemPrices = useCallback(() => {
    return (
      <Price
        total={finalItemPrice ?? ""}
        compareAt={finalCompareAtPrice}
        orientation={isRowAligned || Boolean(isItemPropertiesAvailable) ? "horizontal" : "vertical"}
        size="md"
        customization={{
          textColor: customisations?.textColor,
          freebieTextColor: customisations?.offerBadgeColor,
        }}
      />
    );
  }, [finalCompareAtPrice, finalItemPrice, isCartItem]);

  const getItemProperties = useCallback(() => {
    const subTextStyle = "overflow-hidden text-ellipsis text-text-xs-regular text-zinc-500";
    const subTextColorStyle = {
      color: customisations?.textColor,
      opacity: "60%",
    };

    // Not showing the variant selector if any of the items has item-properties available, filtered according to the merchant's conditions or transformed:
    const renderVariantTitle = () => {
      if (!isVariantSelectorEnabled || Boolean(isC2P) || Boolean(isItemPropertiesAvailable)) {
        return (
          <div className={classNames(subTextStyle, "w-full")} style={subTextColorStyle}>
            {variantTitle}
          </div>
        );
      }
      return (
        <button
          type="button"
          className="flex w-full min-w-0 max-w-fit flex-row items-center gap-1 rounded-md h-[22px] px-1.5"
          style={{
            backgroundColor: hexToRgba(customisations?.textColor?.substring(0, 7) ?? "#000000", 0.06),
          }}
          onClick={(event) => {
            event.stopPropagation();
            setIsVariantSelectorOpen(true);
          }}>
          <span
            style={{ color: hexToRgba(customisations?.textColor?.substring(0, 7) ?? "#000000", 0.6) }}
            className="min-w-0 truncate text-text-xs-medium">
            {variantTitle}
          </span>
          <Icon
            IconComponent={CaretDown}
            size="xxxs"
            iconProps={{
              className: "text-zinc-500 shrink-0",
              color: customisations?.textColor,
              opacity: "60%",
              weight: "fill",
            }}
          />
        </button>
      );
    };

    const renderCartPersonalisationDrawerButton = (
      isStringTypeFirstValueParameterAvailable: boolean,
      combinedLabel: string,
      firstKey: string,
      firstValue: string,
      remainingPropertiesCount: number,
    ) => {
      return (
        <button
          type="button"
          key="item-properties-trigger"
          className={classNames(
            subTextStyle,
            "w-fit",
            "flex items-center gap-1 text-left border-b border-dotted border-b-[2px]",
          )}
          style={{
            borderColor: hexToRgba(customisations?.textColor?.substring(0, 7) ?? "#000000", 0.3),
          }}
          onClick={(event) => {
            event.stopPropagation();
            // Nested so it pauses whatever's active (e.g. OOSDialog, which renders this button
            // for its own items) instead of closing it - it's restored once this one closes.
            openPopup("ITEM_PROPERTIES", { itemId: item_id }, { nested: true });
          }}>
          {isStringTypeFirstValueParameterAvailable ? (
            combinedLabel.length <= 12 ? (
              <span
                style={{
                  color: customisations?.textColor,
                  opacity: "70%",
                }}>
                <span style={{ color: customisations?.textColor, opacity: "60%" }}>{firstKey}</span>{" "}
                {String(firstValue)}
                {remainingPropertiesCount > 0 ? ` +${remainingPropertiesCount} More` : ""}
              </span>
            ) : (
              <span
                style={{
                  color: customisations?.textColor,
                  opacity: "70%",
                }}>
                View details
              </span>
            )
          ) : (
            <span
              style={{
                color: customisations?.textColor,
                opacity: "70%",
              }}>
              View details
            </span>
          )}
        </button>
      );
    };

    const renderPropertiesContent = () => {
      if (variant_title !== "Default Title" && isEmptyObj(properties)) {
        return renderVariantTitle();
      }

      if (isEmptyObj(properties)) return null;

      if (
        variant_title === "Default Title" && //check for a product without any variants - in this case shopify creates the default variant with variant_title as "Default variant"
        !Boolean(layoutMeta?.showAllItemProperties) &&
        !Boolean(itemPropertiesConfig?.length)
      )
        return null;

      try {
        if (variant_title === "Default Title" && isEmptyObj(properties)) return <></>;

        const propertiesList = [];

        if (variant_title !== "Default Title") {
          propertiesList.push(renderVariantTitle());
        }

        if (!isItemPropertyTransformerConfigured) {
          if (Boolean(layoutMeta?.showItemPropertyKey)) {
            for (let key in properties) {
              if (properties.hasOwnProperty(key)) {
                propertiesList.push(
                  <div className={classNames(subTextStyle, "w-full")} style={subTextColorStyle} key={key}>
                    {`${key}: ${properties[key]?.slice(0, 35)}`}
                    {properties[key]?.length > 35 ? "..." : ""}
                  </div>,
                );
              }
            }
            return propertiesList;
          }

          for (let key in properties) {
            if (properties.hasOwnProperty(key)) {
              propertiesList.push(
                <div className={classNames(subTextStyle, "w-full")} style={subTextColorStyle} key={key}>
                  {`${properties[key]?.slice(0, 35)}${properties[key]?.length > 35 ? "..." : ""}`}
                </div>,
              );
            }
          }
          return propertiesList;
        }

        const firstItemProperty = Object.entries(properties)[0] as [string, ItemPropertyValue];
        const [firstKey, firstValue] = firstItemProperty;
        const remainingPropertiesCount = Object.keys(properties).length - 1;
        let combinedLabel = "";
        const isStringTypeFirstValueParameterAvailable =
          firstItemProperty && firstValue !== null && typeof firstValue === "string";

        // Only showing the first key and value for the string type - not for the type where the value can be of type object, because then we will have to check many different cases to get the actual value parameter:
        if (isStringTypeFirstValueParameterAvailable) {
          combinedLabel = `${firstKey} ${firstValue}`;
        }

        propertiesList.push(
          renderCartPersonalisationDrawerButton(
            isStringTypeFirstValueParameterAvailable,
            combinedLabel,
            firstKey,
            firstValue as string,
            remainingPropertiesCount,
          ),
        );

        return propertiesList;
      } catch (e) {
        console.error(e);
        return null;
      }
    };

    if (isBundleItem) {
      return (
        <div className={classNames(subTextStyle, "flex w-full flex-col gap-1")}>
          <div className="flex items-center">
            <span className="text-zinc-500 text-text-xs-regular" style={subTextColorStyle}>
              {components.length} Bundled Items
            </span>
            <div
              className={classNames(
                "transition-transform duration-300 ml-1",
                isExpanded ? "rotate-180" : "rotate-0",
              )}>
              <Icon
                IconComponent={CaretDown}
                size="xs"
                iconProps={{
                  className: "text-zinc-500",
                  color: customisations?.textColor,
                  opacity: "60%",
                }}
              />
            </div>
          </div>
          {renderPropertiesContent()}
        </div>
      );
    }

    return renderPropertiesContent();
  }, [
    item_properties,
    properties,
    merchant,
    variant_title,
    isBundleItem,
    isExpanded,
    isVariantSelectorEnabled,
    isC2P,
    isItemPropertyTransformerConfigured,
    variantTitle,
    customisations,
    item_id,
    openPopup,
  ]);

  const getAddonDetailsForCheckoutItem = (quantity: number) => {
    const addOnDisplayList = appliedItemAddOns?.map((item) => {
      const addOnData = availableAddOns[item?.addon_id];
      return `${truncateString(addOnData?.product_name, 40)} added for ${currencyFormatter(
        addOnData?.price * quantity,
      )}`;
    });
    if (addOnDisplayList?.length === 0) return <></>;
    return (
      <li className="w-full overflow-hidden text-ellipsis text-xs font-normal text-coal-light">
        {addOnDisplayList.join(" / ")}
      </li>
    );
  };

  const getItemHeader = () => {
    return (
      <div className="flex w-full flex-row items-start justify-between space-x-3">
        <LinkButton href={isCartItem && redirectOnClick ? item_url : ""}>
          <p
            className={classNames(
              "overflow-hidden text-ellipsis text-text-xs-medium text-zinc-700",
              isEmptyObj(item_properties) ? "line-clamp-2 text-wrap" : "line-clamp-1 text-wrap",
            )}
            style={{
              color: customisations?.textColor,
            }}>
            {item_title}
          </p>
        </LinkButton>
      </div>
    );
  };

  const getItemPriceElement = useCallback(() => {
    return (
      <>
        {Boolean(disableOOSItems) ? (
          Boolean(is_available) ? (
            <>{getItemPrices()}</>
          ) : (
            <>
              <p
                className="whitespace-nowrap pt-0.5 text-sm font-medium text-ouch"
                style={{ color: customisations?.textColor }}>
                {t("oos_item")}
              </p>
            </>
          )
        ) : (
          getItemPrices()
        )}
      </>
    );
  }, [disableOOSItems, getItemPrices, is_available, t]);

  const getItemActions = () => {
    const isDecrementDisabled = isUpdating || isLoading || !showDecrement || quantity === MIN_ITEM_QUANTITY;
    const isMaxQuantityPerItem = Boolean(
      merchant?.maxQuantityPerItem && quantity + 1 > merchant.maxQuantityPerItem,
    );

    const maxQuantity = Math.min(
      availableQuantity,
      merchant?.maxQuantityPerItem ? merchant.maxQuantityPerItem : availableQuantity,
    );

    const isIncrementDisabled =
      isUpdating ||
      isLoading ||
      !showIncrement ||
      Boolean(quantity + 1 > availableQuantity) ||
      isMaxQuantityPerItem;

    const commonDisabledRules =
      disableOOSItems ||
      Boolean(hasProductSelector) ||
      isUnservicable ||
      isImmutablePriceBand(markupPrice, merchant) ||
      !Boolean(finalItemPrice) ||
      !checkoutItemsMutable ||
      !(Boolean(isMutable) || (isCartItem && !is_freebie && !is_discount_alteration && !is_platform_fee)) ||
      isLoading ||
      isUpdating;

    const wishlistIcon = isWishlistEnabled ? (
      <Icon
        IconComponent={Heart}
        size="md"
        iconProps={{
          className: `cursor-pointer h-4 w-4 ${isItemInWishlist ? "fill-primary-dark" : ""}`,
          strokeWidth: 2.5,
          weight: isItemInWishlist ? "fill" : "regular",
          color: getTextColorByBrightness({
            bgHexColor: customisations?.backgroundColor ?? "#FFFFFF",
            lightOpacity: 60,
            darkOpacity: 60,
          }),
          style: isItemInWishlist ? wishlistIconFillStyle : undefined,
        }}
        onClick={(event) => {
          event.stopPropagation();
          isItemInWishlist ? handleRemoveFromWishlist() : handleAddToWishlist();
        }}
      />
    ) : null;

    const editIcon =
      Boolean(hasProductSelector) && isMutable && !isUnservicable ? (
        <IconButton
          IconComponent={PencilSimple}
          variant="outline"
          size="xs"
          className="!bg-transparent h-7 w-7"
          iconProps={{
            weight: "bold",
            className: "text-zinc-400",
          }}
          onClick={(event) => {
            event.stopPropagation();
            setDiscountProductSelectorModal("DISCOUNTED_PRODUCT_SELECTOR");
          }}
        />
      ) : null;

    const deleteIcon =
      !Boolean(hasProductSelector) &&
      showDelete &&
      ((Boolean(checkoutItemsMutable) && Boolean(isMutable)) ||
        (isCartItem && !is_freebie && !is_discount_alteration && !is_platform_fee)) ? (
        <IconButton
          IconComponent={Trash}
          disabled={isUpdating || isLoading}
          variant="outline"
          size="xs"
          className="!bg-transparent h-7 w-7"
          style={
            {
              "--before-border-color": getTextColorByBrightness({
                bgHexColor: customisations?.backgroundColor ?? "#FFFFFF",
              }),
              "--before-border-color-hover": getTextColorByBrightness({
                bgHexColor: customisations?.backgroundColor ?? "#FFFFFF",
                darkOpacity: 24,
              }),
            } as React.CSSProperties
          }
          iconProps={{
            weight: "bold",
            className: "text-zinc-400 h-3.5 w-3.5",
            color: getTextColorByBrightness({
              bgHexColor: customisations?.backgroundColor ?? "#FFFFFF",
              darkOpacity: 50,
              lightOpacity: 50,
            }),
          }}
          onClick={(event) => {
            event.stopPropagation();
            setOpenRemoveItemDialog(true);
          }}
        />
      ) : null;

    const isDeletable =
      !Boolean(hasProductSelector) &&
      showDelete &&
      ((Boolean(checkoutItemsMutable) && Boolean(isMutable)) ||
        (isCartItem && !is_freebie && !is_discount_alteration && !is_platform_fee));

    const quantitySelector = (
      <QuantitySelector
        customisations={customisations}
        quantity={quantity}
        isCartItem={isCartItem}
        isDecrementDisabled={isDecrementDisabled}
        isIncrementDisabled={isIncrementDisabled}
        commonDisabledRules={commonDisabledRules}
        maxQuantity={maxQuantity}
        isRowAligned={isRowAligned || Boolean(isItemPropertiesAvailable)}
        onDelete={
          (isRowAligned || Boolean(isItemPropertiesAvailable)) && isDeletable
            ? (event) => {
                event.stopPropagation();
                setOpenRemoveItemDialog(true);
              }
            : undefined
        }
        onQuantityChange={(newQuantity) => handleItemEdit("set", { quantity: newQuantity })}
        onDecrement={(event) => {
          event.stopPropagation();
          handleItemEdit("decrement");
        }}
        onIncrement={(event) => {
          event.stopPropagation();
          handleItemEdit("increment");
        }}
        showQuantityEditor={showQuantityEditor}
      />
    );

    // Customised layout: wishlist → edit → quantity, right aligned, no delete.
    // Legacy layout is left untouched: edit → delete → quantity → wishlist.

    return (
      <div
        className={classNames(
          "flex w-full flex-row items-center space-x-2 pt-1",
          isRowAligned || Boolean(isItemPropertiesAvailable) ? "justify-end" : "",
        )}>
        {isRowAligned || Boolean(isItemPropertiesAvailable) ? (
          <>
            {wishlistIcon}
            {editIcon}
            {quantitySelector}
          </>
        ) : (
          <>
            {editIcon}
            {deleteIcon}
            {quantitySelector}
            {wishlistIcon}
          </>
        )}
      </div>
    );
  };

  const handleAddToWishlist = async (deleteItem?: boolean) => {
    if (!isAuthenticated && isCartItem) {
      if (shouldRedirectToParent(merchant?.disableThirdPartyCookieRedirect)) {
        let redirectUrl = new URL(document.location.href);
        let checkoutUrlSearchParams = new URLSearchParams(redirectUrl.search);
        checkoutUrlSearchParams.delete("page");
        checkoutUrlSearchParams.append("checkoutId", checkoutId);
        checkoutUrlSearchParams.append("tokenId", tokenId!);
        redirectUrl.search = checkoutUrlSearchParams.toString();
        publishPostMessage(eventTypes.PARENT_REDIRECT, {
          redirectUrl: redirectUrl.href,
          clearCart: false,
        });
        return;
      }
      setCartDialog("cartAuthentication");
      setCheckoutValidations({
        ...checkoutValidations,
        cartLoginPopup: {
          isValid: false,
          metadata: {
            message: "Verify your phone number to wishlist the item",
          },
        },
      });
      return;
    }
    if (!isAuthenticated) {
      infoToast(t("login_to_add_to_wishlist"), 3000);
      return;
    }
    try {
      const success = await handleWishlistAddItem(
        merchant?.merchantId ?? "",
        user?.uid ?? "",
        { productId: product_id, variantId: variant_id },
        "CART",
      );
      if (success) {
        successToast(t("added_to_wishlist"), 3000);
        if (Boolean(deleteItem)) {
          handleItemEdit("delete");
        }
        sendAnalyticsEvent({
          eventName: analyticsEvents.ADDED_TO_WISHLIST,
          eventType: "flo_action",
          metaData: {
            wishlistData: {
              variantId: variant_id,
              variantName: variant_title,
              productId: item_id,
              productName: item_title,
              parent: isCartItem ? "CART" : "CHECKOUT",
            },
          },
        });
      } else {
        errorToast(t("error_adding_to_wishlist"), 3000);
        if (Boolean(deleteItem)) {
          handleItemEdit("delete");
        }
      }
    } finally {
      refreshWishlist();
    }
  };

  const handleRemoveFromWishlist = async () => {
    try {
      const success = await handleWishlistRemoveItem(
        merchant?.merchantId ?? "",
        user?.uid ?? "",
        { productId: product_id, variantId: variant_id },
        "CART",
      );
      if (success) {
        successToast(t("removed_from_wishlist"), 3000);
        sendAnalyticsEvent({
          eventName: analyticsEvents.REMOVED_FROM_WISHLIST,
          eventType: "flo_action",
          metaData: {
            wishlistData: {
              variantId: variant_id,
              variantName: variant_title,
              productId: item_id,
              productName: item_title,
              parent: isCartItem ? "CART" : "CHECKOUT",
            },
          },
        });
      } else {
        errorToast(t("error_removing_from_wishlist"), 3000);
      }
    } finally {
      refreshWishlist();
    }
  };
  const isAddOnLineItemsVisible = showAddOnLineItems(availableAddOns, appliedItemAddOns);

  const itemETDConfig = itemLevelETD?.find((etd) => etd.variantId === variant_id);
  const edtConfigMessage = itemETDConfig?.edt_message;
  const itemMaxDate = useMemo(() => {
    if (edtConfigMessage) return;
    const raw = itemETDConfig?.date?.max;
    if (!raw) return null;
    const date = new Date(`${raw}T00:00:00`);
    if (isNaN(date.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.round((date.getTime() - today.getTime()) / 86400000);
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Tomorrow";
    return date.toDateString();
  }, [itemETDConfig]);

  const imageUrl: string = useMemo(() => {
    return getModifiedImageUrl(image, defaultPackageImage);
  }, [image]);

  const selectedProductAddon =
    itemLevelAddons?.filter((item) => {
      return !appliedItemAddOns.some((appliedItem) => appliedItem?.addon_id === item?.addon_id);
    }) ?? [];

  // "Add to cart by default" add-ons must attach themselves the moment their trigger
  // item is eligible, without waiting for the customer to press the "+Add" row.
  // selectedProductAddon is a new array every render, so without a guard this effect
  // would re-fire ADD on every render while a request is in flight - autoAddedAddonIds
  // blocks that. It's only cleared on a successful add, so eligibility is re-checked for
  // any later render (eg. the add-on becoming unapplied again for reasons unrelated to the
  // customer, such as an unrelated cart line being removed). On failure (eg. the add-on
  // going out of stock) the guard is left in place - clearing it there would re-trigger
  // the same doomed request on every subsequent re-render, spamming the endpoint.
  // userRemovedAddonIds is the permanent guard: it must never re-fire for an addon the
  // customer explicitly removed via the "remove" action on this item.
  const autoAddedAddonIds = useRef<Set<string>>(new Set());
  const userRemovedAddonIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (is_freebie || isSubscribeMode || !checkoutItemsMutable || isC2P) return;
    selectedProductAddon
      .filter((addon) => {
        if (
          !addon?.default_selected ||
          autoAddedAddonIds.current.has(addon.addon_id) ||
          userRemovedAddonIds.current.has(addon.addon_id)
        )
          return false;
        const rawAddonEntry = rawItem?.item_addons?.find(
          (rawAddon: any) => rawAddon?.addon_id === addon.addon_id,
        );
        return !rawAddonEntry?.removed;
      })
      .forEach((addon) => {
        autoAddedAddonIds.current.add(addon.addon_id);
        handleAddOnEdit({
          action: "ADD",
          addOnLevel: "ITEM",
          addOnId: addon.addon_id,
          addOnPrice: addon.price,
        }).then((success) => {
          if (success) {
            autoAddedAddonIds.current.delete(addon.addon_id);
          }
        });
      });
  }, [selectedProductAddon, is_freebie, isSubscribeMode, checkoutItemsMutable, isC2P, rawItem]);

  return (
    <>
      <div
        onClick={(event) => {
          event.stopPropagation();
          if (isBundleItem) {
            setIsExpanded(!isExpanded);
          }
        }}
        style={{
          backgroundColor:
            isBundleItem && isExpanded && !isCartItem
              ? hexToRgba(merchant?.colorPallet?.primaryColor ?? "#01CCA70F", 0.1, "#01CCA70F")
              : "",
        }}
        className={classNames(
          `flex w-full min-w-full flex-row justify-between transition-all duration-300 relative z-2`,
          isUnservicable ? "bg-gray-light rounded-2xl p-1.5 pr-2" : "",
          itemLevelAddons && itemLevelAddons.length > 0 ? "rounded-t-xl" : "rounded-xl",
          isBundleItem ? "cursor-pointer" : "",
          isBundleItem && isExpanded && (isCartItem || isUnservicable) ? "rounded-b-none" : "",
        )}>
        <div className="flex w-full space-x-3 pr-1">
          <div
            className={classNames(
              "relative flex items-start justify-center ",
              isCartItem ? "min-h-[72px] min-w-[72px]" : "h-[72px] w-[72px]",
            )}>
            {is_freebie ? (
              <div
                className="absolute top-[1px] right-[1px] flex h-6 w-6 items-center justify-center rounded-md rounded-tl-none rounded-br-none bg-yay-dark shadow-sm"
                style={{
                  backgroundColor: customisations?.offerBadgeColor,
                }}>
                <Icon IconComponent={Gift} size="xxs" iconProps={{ className: "text-white" }} />
              </div>
            ) : (
              <></>
            )}
            <LinkButton href={isCartItem && redirectOnClick ? item_url : ""}>
              <div
                className={classNames(
                  "overflow-hidden border-1 border-gray-light rounded-lg h-[72px] w-[72px]",
                  isUnservicable ? "rounded-2xl grayscale" : "rounded-lg",
                )}
                style={{
                  borderColor: getTextColorByBrightness({
                    bgHexColor: customisations?.backgroundColor ?? "#FFFFFF",
                    lightOpacity: 24,
                  }),
                }}>
                <img
                  src={imageUrl}
                  alt="Cart Item"
                  className={classNames(
                    `object-cover w-full h-full`,
                    disableOOSItems && !is_available ? "grayscale" : "",
                  )}
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null; // prevents looping
                    currentTarget.src = defaultPackageImage;
                  }}
                />
              </div>
            </LinkButton>
          </div>
          <ul className="flex min-w-0 grow flex-col items-center justify-center min-h-[72px]">
            {isRowAligned || Boolean(isItemPropertiesAvailable) ? (
              <>
                <div className="flex gap-6 justify-between w-full h-full">
                  <div className="flex flex-col gap-1 min-w-0 flex-1 text-start">
                    {getItemHeader()}
                    {getItemProperties()}
                  </div>
                  <div className="flex flex-col items-end shrink-0">
                    {getItemPriceElement()}
                    {getItemActions()}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex w-full h-full justify-between items-start gap-2 ">
                <div className="min-w-0 flex-1">
                  {getItemHeader()}
                  {getItemProperties()}
                </div>
                <div className="shrink-0">{getItemPriceElement()}</div>
              </div>
            )}
            {!isCartItem &&
              !isAddOnLineItemsVisible &&
              checkoutItemsMutable &&
              getAddonDetailsForCheckoutItem(quantity)}
            {!isRowAligned && !Boolean(isItemPropertiesAvailable) && getItemActions()}
          </ul>
        </div>
      </div>
      {Boolean(edtConfigMessage || itemMaxDate) && (
        <div className="flex pl-[5.375rem] justify-start items-center text-zinc-500 space-x-2 py-1">
          <ShippingDeliveryIcon icon={ShippingDeliveryIconType.Truck} />
          <p className="text-text-xs-regular">
            {edtConfigMessage ? (
              edtConfigMessage
            ) : (
              <>
                Delivered by <span className="text-text-xs-semibold">{itemMaxDate}</span>
              </>
            )}
          </p>
        </div>
      )}
      {isBundleItem && isExpanded && (
        <div
          className={classNames(
            "flex flex-col gap-2 pl-8 py-2 ",
            isUnservicable ? "bg-gray-light rounded-b-xl" : "",
            isCartItem && !is_discount_alteration ? "" : "rounded-b-xl",
          )}>
          {components.map((component) => {
            return (
              <BundleItem
                key={component.item_id}
                {...component}
                variant_name={component.variant_title}
                isCartItem={isCartItem}
                isUnservicable={isUnservicable}
                customisations={customisations}
              />
            );
          })}
        </div>
      )}

      {!is_freebie && !isSubscribeMode && checkoutItemsMutable && !isC2P && selectedProductAddon?.length > 0 && (
        <div className="pl-8 pt-2.5 space-y-2">
          {selectedProductAddon?.map((item, index) => {
            return (
              <AddOnSelectItem
                customisations={customisations}
                apiActionLoading={isLoading}
                addOnData={availableAddOns[item?.addon_id]}
                variant="ITEM"
                isCartItem={isCartItem}
                itemQuantity={quantity}
                handleButtonClick={() => {
                  handleAddOnEdit({
                    action: "ADD",
                    addOnLevel: "ITEM",
                    addOnId: item?.addon_id,
                    addOnPrice: item?.price,
                  });
                }}
                isItemPropertiesAvailable={Boolean(isItemPropertiesAvailable)}
              />
            );
          })}
        </div>
      )}

      {isAddOnLineItemsVisible && checkoutItemsMutable && appliedItemAddOns?.length > 0 && (
        <ul
          className="mt-5 space-y-5 [&>li]:relative [&>li]:before:content-[''] [&>li]:before:absolute [&>li]:before:left-0 [&>li]:before:right-0 [&>li]:before:top-[-10px] [&>li]:before:h-[0.5px] [&>li]:before:bg-[var(--divider-color)]"
          style={{
            ["--divider-color" as any]: getTextColorByBrightness({
              bgHexColor: customisations?.backgroundColor ?? "#FFFFFF",
              lightOpacity: 24,
            }),
          }}>
          {appliedItemAddOns?.map((item, index) => {
            return (
              <li key={item?.addon_id}>
                <AddOnItem
                  customisations={customisations}
                  allowDelete={isCartItem}
                  itemQuantity={quantity}
                  key={item?.addon_id}
                  variant={isCartItem ? "CART" : "CHECKOUT"}
                  addOnData={availableAddOns[item?.addon_id]}
                  parentTitle={item_title}
                  disableActions={isLoading || Boolean(isC2P)}
                  handleAddOnDelete={
                    isC2P
                      ? undefined
                      : () => {
                          if (item?.addon_id) {
                            userRemovedAddonIds.current.add(item.addon_id);
                          }
                          handleAddOnEdit({
                            action: "DELETE",
                            addOnLevel: "ITEM",
                            addOnId: item?.addon_id,
                            addOnPrice: item.price,
                          });
                        }
                  }
                  isItemPropertiesAvailable={Boolean(isItemPropertiesAvailable)}
                />
              </li>
            );
          })}
        </ul>
      )}

      {isVariantSelectorEnabled && (
        <ItemVariantSelectorDialog
          isOpen={isVariantSelectorOpen}
          onClose={() => setIsVariantSelectorOpen(false)}
          productName={item_title}
          imageUrl={imageUrl}
          variants={productVariants}
          selectedVariantId={variant_id}
          isLoading={isLoading || isUpdating}
          onConfirm={async (newVariantId, newVariantAvailableQuantity) => {
            await handleVariantSwap(newVariantId, newVariantAvailableQuantity);
            setIsVariantSelectorOpen(false);
          }}
        />
      )}

      <GenericDialog
        isOpen={openRemoveItemDialog}
        setIsOpen={() => setOpenRemoveItemDialog(false)}
        translateAxis="y"
        customClass="overflow-scroll md:!top-auto md:absolute"
        dialogOverlay={true}
        modalType="REMOVE_ITEM">
        <RemoveItemDialog
          setIsOpen={() => setOpenRemoveItemDialog(false)}
          onConfirm={handleItemDeleteSubmit}
          isLoading={isLoading}
          itemProps={{
            ...props,
          }}
        />
      </GenericDialog>

      <GenericDialog
        isOpen={isItemPropertiesDialogOpen}
        showExternalClose={true}
        setIsOpen={() => closePopup()}
        translateAxis="y"
        customClass={classNames("flex flex-col max-h-[70%]", image ? "h-full" : "")}
        childClassName="min-h-0"
        dialogOverlay={true}
        closeOnOverlayClick={true}
        modalType="ITEM_PROPERTIES"
        disableExitAnimation={activePopup !== null}>
        <ItemPropertiesDialog
          itemDetails={itemPropertiesDialogDetails}
          itemProperties={properties}
          onImageClick={handlePropertyImageClick}
        />
      </GenericDialog>

      <FullPageCarousel
        isOpen={Boolean(propertyImagePreview)}
        onClose={() => setPropertyImagePreview(null)}
        totalSlides={propertyImagePreview?.images.length ?? 0}
        startIndex={propertyImagePreview?.startIndex ?? 0}>
        {propertyImagePreview?.images.map((src, index) => (
          <CarouselItem key={index} className="flex items-center">
            <div className="h-[80%] w-full flex items-center justify-center">
              <img
                src={src}
                alt={`${src} - Image ${index + 1}`}
                className="object-contain h-[80%] max-w-full"
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null; // prevents looping
                  currentTarget.src = defaultPackageImage;
                }}
              />
            </div>
          </CarouselItem>
        ))}
      </FullPageCarousel>

      {((!isCartItem && isLoading) || showFullScreenLoader) && <OverlaySpinner />}
    </>
  );
};
